import axios, { AxiosInstance } from 'axios';
import {
  AirtableCustomer,
  SelfIPA,
  BonusInfo,
  ShippingTracking,
  Ticket,
} from '../types/airtable';

const AIRTABLE_TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;
const BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID;
const BASE_URL = `https://api.airtable.com/v0/${BASE_ID}`;

// Axios instance with auth
const airtableAPI: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    Authorization: `Bearer ${AIRTABLE_TOKEN}`,
    'Content-Type': 'application/json',
  },
});

/**
 * Airtable Service - READ-ONLY
 * All functions are for reading data only
 */
export const AirtableService = {
  /**
   * Login: search customer by email
   * @param email - Customer's email
   * @returns Customer data or null
   */
  async loginByEmail(email: string): Promise<AirtableCustomer | null> {
    try {
      const formula = `{Email} = "${email}"`;
      const response = await airtableAPI.get('/tbl01SsG1bzvhWDN6', {
        params: {
          filterByFormula: formula,
          maxRecords: 1,
        },
      });

      if (response.data.records.length > 0) {
        const record = response.data.records[0];
        return {
          id: record.id,
          uid: record.fields.Uid,
          nome:
            record.fields['Contratto: Nome e cognome'] ||
            record.fields['Contact[First Name]'],
          cognome: record.fields['Contact[Last Name]'],
          email: record.fields.Email,
          telefono: record.fields['Numero telefono'],
          indirizzo: record.fields['Indirizzo Impianto'],
          installStatus: record.fields['Install Status'],
          tipoProdotto: record.fields['Tipo Prodotto & Incentivo'],
          projectManager: record.fields['Project Manager']?.name,
          buildabilityOutcome: record.fields['Buildability outcome'],
          ipaAppointment: record.fields['IPA appointment'],
          dataFirmaContratto: record.fields['Data firma contratto'],
          ...record.fields,
        };
      }
      return null;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error('Errore durante il login. Riprova.');
    }
  },

  /**
   * Get complete customer data
   * @param uid - Customer UID
   * @returns Customer data
   */
  async getCustomerData(uid: string): Promise<AirtableCustomer | null> {
    try {
      const formula = `{Uid} = "${uid}"`;
      const response = await airtableAPI.get('/tbl01SsG1bzvhWDN6', {
        params: {
          filterByFormula: formula,
          maxRecords: 1,
        },
      });

      if (response.data.records.length > 0) {
        const record = response.data.records[0];
        return {
          id: record.id,
          uid: record.fields.Uid,
          nome:
            record.fields['Contratto: Nome e cognome'] ||
            record.fields['Contact[First Name]'],
          email: record.fields.Email,
          ...record.fields,
        };
      }
      return null;
    } catch (error) {
      console.error('Get customer data error:', error);
      throw error;
    }
  },

  /**
   * Get Self IPA PV records for customer
   * @param customerRecordId - Customer record ID
   * @returns Array of Self IPA
   */
  async getSelfIPAPV(customerRecordId: string): Promise<SelfIPA[]> {
    try {
      const response = await airtableAPI.get('/Self IPA PV', {
        params: {
          filterByFormula: `SEARCH("${customerRecordId}", ARRAYJOIN({Customer base}))`,
        },
      });
      return response.data.records.map(
        (r: { id: string; fields: Record<string, unknown> }) => ({
          id: r.id,
          ...r.fields,
        })
      );
    } catch (error) {
      console.error('Get Self IPA PV error:', error);
      return [];
    }
  },

  /**
   * Get Self IPA HP (Heat Pump) records
   * @param customerRecordId - Customer record ID
   * @returns Array of Self IPA HP
   */
  async getSelfIPAHP(customerRecordId: string): Promise<SelfIPA[]> {
    try {
      const response = await airtableAPI.get('/Self IPA HP', {
        params: {
          filterByFormula: `SEARCH("${customerRecordId}", ARRAYJOIN({Customer base}))`,
        },
      });
      return response.data.records.map(
        (r: { id: string; fields: Record<string, unknown> }) => ({
          id: r.id,
          ...r.fields,
        })
      );
    } catch (error) {
      console.error('Get Self IPA HP error:', error);
      return [];
    }
  },

  /**
   * Get customer tickets/claims
   * @param uid - Customer UID
   * @returns Array of tickets
   */
  async getTickets(uid: string): Promise<Ticket[]> {
    try {
      const response = await airtableAPI.get('/Ticket/Claims', {
        params: {
          filterByFormula: `{UID} = "${uid}"`,
        },
      });
      return response.data.records.map(
        (r: { id: string; fields: Record<string, unknown> }) => ({
          id: r.id,
          ...r.fields,
        })
      );
    } catch (error) {
      console.error('Get tickets error:', error);
      return [];
    }
  },

  /**
   * Get bonus/incentive info (ENEA/PNRR/RedEn)
   * @param uid - Customer UID
   * @returns Array of bonus info
   */
  async getBonusInfo(uid: string): Promise<BonusInfo[]> {
    try {
      const response = await airtableAPI.get('/ENEA/PNRR/RedEn', {
        params: {
          filterByFormula: `{UID} = "${uid}"`,
        },
      });
      return response.data.records.map(
        (r: { id: string; fields: Record<string, unknown> }) => ({
          id: r.id,
          ...r.fields,
        })
      );
    } catch (error) {
      console.error('Get bonus info error:', error);
      return [];
    }
  },

  /**
   * Get available installation slots
   * @returns Array of slots
   */
  async getInstallationSlots(): Promise<unknown[]> {
    try {
      const response = await airtableAPI.get('/New HWP Slot', {
        params: {
          maxRecords: 50,
        },
      });
      return response.data.records.map(
        (r: { id: string; fields: Record<string, unknown> }) => ({
          id: r.id,
          ...r.fields,
        })
      );
    } catch (error) {
      console.error('Get installation slots error:', error);
      return [];
    }
  },

  /**
   * Get shipping tracking
   * @param uid - Customer UID
   * @returns Array of tracking info
   */
  async getShippingTracking(uid: string): Promise<ShippingTracking[]> {
    try {
      const response = await airtableAPI.get('/Tracking Spedizioni Enpal', {
        params: {
          filterByFormula: `{UID} = "${uid}"`,
        },
      });
      return response.data.records.map(
        (r: { id: string; fields: Record<string, unknown> }) => ({
          id: r.id,
          ...r.fields,
        })
      );
    } catch (error) {
      console.error('Get shipping tracking error:', error);
      return [];
    }
  },
};

export default AirtableService;
